import React, { useMemo } from 'react';

interface RhythmPrintVizProps {
  /** Embedding vector (array of floats, typically 64 or 128 values) */
  embedding: number[];
  /** Size of the SVG */
  size?: number;
  /** Primary color */
  color?: string;
  /** Secondary color for gradient */
  secondaryColor?: string;
  /** Show ring labels */
  showLabels?: boolean;
  /** Animation enabled */
  animate?: boolean;
  /** Number of rings */
  rings?: number;
}

/**
 * RhythmPrintViz — Artistic radial visualization of a biometric embedding.
 * Creates a unique "fingerprint" SVG from the embedding vector.
 */
export default function RhythmPrintViz({
  embedding,
  size = 280,
  color = '#7c5cfc',
  secondaryColor = '#4fc3f7',
  showLabels = false,
  animate = true,
  rings = 5,
}: RhythmPrintVizProps) {
  const center = size / 2;
  const maxRadius = size * 0.42;
  const minRadius = size * 0.12;

  // Generate the radial paths from embedding data
  const paths = useMemo(() => {
    if (embedding.length === 0) return [];

    const result: Array<{
      d: string;
      color: string;
      opacity: number;
      strokeWidth: number;
    }> = [];

    const pointsPerRing = Math.floor(embedding.length / rings);

    for (let ring = 0; ring < rings; ring++) {
      const baseRadius = minRadius + ((maxRadius - minRadius) / rings) * (ring + 1);
      const startIdx = ring * pointsPerRing;
      const endIdx = Math.min(startIdx + pointsPerRing, embedding.length);
      const ringData = embedding.slice(startIdx, endIdx);

      if (ringData.length < 3) continue;

      const numPoints = ringData.length;
      const angleStep = (2 * Math.PI) / numPoints;

      // Generate points on this ring with radius modulated by embedding values
      const points: Array<{ x: number; y: number }> = [];
      for (let i = 0; i < numPoints; i++) {
        const angle = angleStep * i - Math.PI / 2;
        const amplitude = baseRadius * 0.15;
        const r = baseRadius + ringData[i] * amplitude;
        points.push({
          x: center + r * Math.cos(angle),
          y: center + r * Math.sin(angle),
        });
      }

      // Create smooth closed path using Catmull-Rom spline
      let d = `M ${points[0].x},${points[0].y}`;
      for (let i = 0; i < points.length; i++) {
        const p0 = points[(i - 1 + points.length) % points.length];
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        const p3 = points[(i + 2) % points.length];

        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }
      d += ' Z';

      // Interpolate color between primary and secondary
      const t = ring / (rings - 1);
      const opacity = 0.3 + 0.5 * (1 - t);
      const strokeWidth = 1.5 + (1 - t);

      result.push({
        d,
        color: t < 0.5 ? color : secondaryColor,
        opacity,
        strokeWidth,
      });
    }

    return result;
  }, [embedding, size, rings, color, secondaryColor, center, maxRadius, minRadius]);

  // Generate decorative dots at embedding peaks
  const dots = useMemo(() => {
    if (embedding.length === 0) return [];
    return embedding
      .map((val, i) => {
        const angle = (2 * Math.PI * i) / embedding.length - Math.PI / 2;
        const r = minRadius + (maxRadius - minRadius) * ((Math.abs(val) + 1) / 2);
        return {
          x: center + r * Math.cos(angle),
          y: center + r * Math.sin(angle),
          r: 1 + Math.abs(val) * 1.5,
          opacity: 0.3 + Math.abs(val) * 0.4,
        };
      })
      .filter((_, i) => i % 3 === 0); // Show every 3rd dot
  }, [embedding, center, maxRadius, minRadius]);

  const gradientId = `rhythm-gradient-${React.useId()}`;
  const glowId = `rhythm-glow-${React.useId()}`;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={animate ? 'transition-all duration-500' : ''}
      >
        <defs>
          <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={color} stopOpacity="0.1" />
            <stop offset="100%" stopColor={secondaryColor} stopOpacity="0.02" />
          </radialGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background glow */}
        <circle cx={center} cy={center} r={maxRadius + 10} fill={`url(#${gradientId})`} />

        {/* Reference circles */}
        {Array.from({ length: rings }, (_, i) => {
          const r = minRadius + ((maxRadius - minRadius) / rings) * (i + 1);
          return (
            <circle
              key={`ref-${i}`}
              cx={center}
              cy={center}
              r={r}
              fill="none"
              stroke="#646681"
              strokeWidth="0.5"
              strokeDasharray="2 4"
              opacity="0.2"
            />
          );
        })}

        {/* Radial paths */}
        {paths.map((path, i) => (
          <path
            key={i}
            d={path.d}
            fill="none"
            stroke={path.color}
            strokeWidth={path.strokeWidth}
            opacity={path.opacity}
            filter={`url(#${glowId})`}
            className={animate ? 'transition-all duration-300' : ''}
          />
        ))}

        {/* Dots */}
        {dots.map((dot, i) => (
          <circle
            key={`dot-${i}`}
            cx={dot.x}
            cy={dot.y}
            r={dot.r}
            fill={color}
            opacity={dot.opacity}
          />
        ))}

        {/* Center dot */}
        <circle cx={center} cy={center} r="4" fill={color} opacity="0.8" />
        <circle cx={center} cy={center} r="2" fill="white" opacity="0.6" />

        {/* Label */}
        {showLabels && (
          <text
            x={center}
            y={size - 8}
            textAnchor="middle"
            fill="#646681"
            fontSize="10"
            fontFamily="Inter, sans-serif"
          >
            RhythmPrint
          </text>
        )}
      </svg>
    </div>
  );
}
